import React, { useState } from "react";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import Popover from "@mui/material/Popover";
import { formatDateReadableWithDayOfWeek } from "../../requests/utilities/helpers";

function getWeatherIconPath(iconString) {
  if (!iconString) return null;
  const iconFileName = iconString.split("/").pop();
  return iconFileName
    ? `${process.env.PUBLIC_URL}/images/weather/${iconFileName}`
    : null;
}

function formatTemperature(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return `${Math.round(numericValue)}°F`;
}

function formatEventTime(value) {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  const hour = Math.floor(numericValue);
  const minutes = Math.round((numericValue - hour) * 60);
  const date = new Date(2000, 0, 1, hour, minutes);
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: minutes ? "2-digit" : undefined,
  });
}

function HistoricalSalesContextCard(props) {
  const [notesAnchorEl, setNotesAnchorEl] = useState(null);
  const canCompareToActual = Boolean(props.actual_sales);
  const events = Array.isArray(props.events) ? props.events : [];
  const weather = Array.isArray(props.weather)
    ? props.weather[0]
    : props.weather;
  const weatherIconPath = getWeatherIconPath(weather?.icon);
  const weatherTemperature = formatTemperature(weather?.maxtemp);
  const showWeather = Boolean(weatherIconPath || weatherTemperature);
  const holidayName = props.holidayName || "Holiday";
  const firstEvent = events[0];
  const formattedDate = props.date
    ? formatDateReadableWithDayOfWeek(props.date)
    : "this date";
  const comparisonIcon =
    Math.abs(props.actual_sales - props.adjusted_forecast) <=
    Math.abs(props.actual_sales - props.initial_forecast) ? (
      <span className="text-green-600 text-sm">👍</span>
    ) : (
      <span className="text-rose-700 text-sm">👎</span>
    );

  let actualColor = "";
  if (props.adjusted_forecast) {
    if (props.adjusted_forecast <= props.actual_sales) {
      actualColor = "bg-emerald-50 text-emerald-700";
    } else {
      actualColor = "bg-rose-50 text-rose-700";
    }
  } else {
    if (props.initial_forecast <= props.actual_sales) {
      actualColor = "bg-emerald-50 text-emerald-700";
    } else {
      actualColor = "bg-rose-50 text-rose-700";
    }
  }

  return (
    <div className="relative rounded-lg border border-slate-200 px-3 py-2.5 bg-white">
      {props.holiday && (
        <span
          className="absolute right-3 top-2.5 inline-flex max-w-[140px] items-center rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-medium text-amber-700 border border-amber-200"
          title={holidayName}
        >
          <span className="block min-w-0 truncate">{holidayName}</span>
        </span>
      )}
      <div className={`mb-2 min-w-0 ${props.holiday ? "pr-[150px]" : ""}`}>
        <div className="flex min-w-0 items-center gap-1.5">
          <p className="min-w-0 truncate text-[10px] text-slate-500">
            {props.date ? formatDateReadableWithDayOfWeek(props.date) : ""}
          </p>
          {showWeather && (
            <div
              className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-slate-500"
              title={weather?.text || undefined}
            >
              {weatherIconPath && (
                <img
                  className="h-4 w-4"
                  src={weatherIconPath}
                  alt={weather?.text || "Weather"}
                />
              )}
              {weatherTemperature && <span>{weatherTemperature}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] text-slate-500">Forecasted</p>
          {props.initial_forecast ? (
            <p className="text-sm font-bold text-slate-900">
              {Number(props.initial_forecast).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </p>
          ) : (
            <p className="text-sm font-bold text-slate-400">No data</p>
          )}
        </div>
        {props.adjusted_forecast && (
          <div>
            <p className="text-[9px] text-slate-500">Adjusted</p>
            <div className="flex items-center gap-1">
              <p className="text-sm font-bold text-slate-900">
                {Number(props.adjusted_forecast).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </p>
              <span
                className={`inline-flex w-4 justify-center ${
                  canCompareToActual ? "" : "invisible"
                }`}
                aria-hidden={!canCompareToActual}
              >
                {comparisonIcon}
              </span>
            </div>
          </div>
        )}
        <div className="text-right">
          <p className="text-[9px] text-slate-500">Actual</p>
          {props.actual_sales ? (
            <p
              className={`text-sm font-bold px-1.5 py-0.5 rounded ${actualColor}`}
            >
              {Number(props.actual_sales).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </p>
          ) : (
            <p className="text-sm font-bold text-slate-400">No data</p>
          )}
        </div>
      </div>
      {events.length > 0 && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <button
            type="button"
            className="flex w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-left text-[10px] text-violet-700 transition-colors hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-violet-200"
            aria-label={`View ${events.length} historical ${events.length === 1 ? "note" : "notes"} for ${formattedDate}`}
            aria-haspopup="dialog"
            aria-expanded={Boolean(notesAnchorEl)}
            onClick={(event) => setNotesAnchorEl(event.currentTarget)}
          >
            <EventNoteOutlinedIcon sx={{ fontSize: 14 }} className="shrink-0" />
            <span
              className="min-w-0 flex-1 truncate font-medium"
              title={
                firstEvent?.title
                  ? `${firstEvent.title}${firstEvent.description ? ` — ${firstEvent.description}` : ""}`
                  : firstEvent?.description || undefined
              }
            >
              {firstEvent?.title || firstEvent?.description || "Historical note"}
            </span>
            {events.length > 1 && (
              <span className="shrink-0 text-[9px] font-semibold text-violet-600">
                {events.length} notes
              </span>
            )}
          </button>
          <Popover
            open={Boolean(notesAnchorEl)}
            anchorEl={notesAnchorEl}
            onClose={() => setNotesAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 0.5,
                width: 320,
                maxWidth: "calc(100vw - 32px)",
                borderRadius: "10px",
              },
            }}
          >
            <div className="p-3">
              <p className="text-xs font-semibold text-slate-900">
                {events.length} {events.length === 1 ? "note" : "notes"} on {formattedDate}
              </p>
              <div className="mt-2 divide-y divide-slate-100">
                {events.map((historicalEvent, index) => {
                  const startTime = formatEventTime(historicalEvent.start_time);
                  const endTime = formatEventTime(historicalEvent.end_time);
                  return (
                    <div key={historicalEvent.id || index} className="py-2 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-xs font-medium text-slate-900">
                          {historicalEvent.title || "Untitled note"}
                        </p>
                        {startTime && (
                          <span className="shrink-0 text-[10px] text-slate-500">
                            {startTime}{endTime ? ` – ${endTime}` : ""}
                          </span>
                        )}
                      </div>
                      {historicalEvent.description && (
                        <p className="mt-1 whitespace-pre-wrap text-[11px] leading-4 text-slate-600">
                          {historicalEvent.description}
                        </p>
                      )}
                      {historicalEvent.visible_to_employees === false && (
                        <span className="mt-1.5 inline-flex rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
                          Hidden from employees
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Popover>
        </div>
      )}
    </div>
  );
}

export default HistoricalSalesContextCard;
